import { createIdempotencyKey } from "@lafayette/api-client";
import type {
  StaffCatalogLocationConfirmBody,
  StaffCatalogLocationResult,
  StaffCatalogPlaceClearBody,
  StaffCatalogPlaceSelectBody,
  StaffCatalogPlaceVerificationQueued,
  StaffCatalogPlaceVerifyBody,
  StaffCatalogPlaceView,
} from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import type { SaveMessage } from "../commands";
import type { CatalogFailure } from "../errors";
import { catalogKeys, useItemFetcher } from "../queries";
import { useCatalogRuntime } from "../runtime";
import { describePlaceError, type PlaceAction } from "./errors";
import { placeKeys } from "./queries";

// Runs the place commands P3 to P6 and keeps the cached item and place view current. The rules are the
// ones of `useCatalogCommand` (one idempotency key per user action, kept for a retry after a failure where
// the command may run again; a 409 reads the item again and compares `version`):
//   - select and clear (P3, P4) return the place view; the item's version moved, so the item is read again.
//   - location (P6) returns the new detail and the new view; both go into the cache.
//   - verify (P5) changes no item column and needs no version.
// Search (P2) is NOT here: it is not idempotent, and its answer is provider content that only the search
// panel may hold.

type Inputs = {
  select: StaffCatalogPlaceSelectBody;
  clear: StaffCatalogPlaceClearBody;
  verify: StaffCatalogPlaceVerifyBody;
  location: StaffCatalogLocationConfirmBody;
};
type Results = {
  select: StaffCatalogPlaceView;
  clear: StaffCatalogPlaceView;
  verify: StaffCatalogPlaceVerificationQueued;
  location: StaffCatalogLocationResult;
};
export type PlaceCommandName = keyof Inputs;

export type PlaceConflict = Readonly<{
  changedElsewhere: boolean;
  sentVersion: number;
  latestVersion: number | null;
}>;

export type PlaceOutcome<K extends PlaceCommandName> =
  | Readonly<{ ok: true; result: Results[K] }>
  | Readonly<{ ok: false; failure: CatalogFailure; conflict: PlaceConflict | null }>;

type PlaceCommandContext = Readonly<{ lookupAvailable: boolean }>;

export function usePlaceCommand(itemId: string) {
  const { api } = useCatalogRuntime();
  const queryClient = useQueryClient();
  const fetchItem = useItemFetcher();
  const pendingKey = useRef<{ signature: string; key: string } | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  const refreshItem = useCallback(async () => {
    const latest = await fetchItem(itemId);
    queryClient.setQueryData(catalogKeys.item(itemId), latest);
    return latest;
  }, [fetchItem, itemId, queryClient]);

  const readConflict = useCallback(
    async (sentVersion: number): Promise<PlaceConflict> => {
      try {
        const latest = await refreshItem();
        return {
          changedElsewhere: latest.item.version !== sentVersion,
          sentVersion,
          latestVersion: latest.item.version,
        };
      } catch {
        return { changedElsewhere: false, sentVersion, latestVersion: null };
      }
    },
    [refreshItem],
  );

  const run = useCallback(
    async <K extends PlaceCommandName>(
      command: K,
      input: Inputs[K],
      context: PlaceCommandContext,
    ): Promise<PlaceOutcome<K>> => {
      const signature = JSON.stringify([command, itemId, input]);
      const key =
        pendingKey.current?.signature === signature
          ? pendingKey.current.key
          : createIdempotencyKey();
      pendingKey.current = { signature, key };
      const options = { idempotencyKey: key };

      setPendingCount((count) => count + 1);
      try {
        let result: Results[K];
        switch (command) {
          case "select": {
            const view = await api.selectPlace(itemId, input as Inputs["select"], options);
            queryClient.setQueryData(placeKeys.view(itemId, view.itemVersion), view);
            await refreshItem().catch(() => undefined);
            result = view as Results[K];
            break;
          }
          case "clear": {
            const view = await api.clearPlace(itemId, input as Inputs["clear"], options);
            queryClient.setQueryData(placeKeys.view(itemId, view.itemVersion), view);
            await refreshItem().catch(() => undefined);
            result = view as Results[K];
            break;
          }
          case "verify": {
            result = (await api.requestPlaceVerification(
              itemId,
              input as Inputs["verify"],
              options,
            )) as Results[K];
            break;
          }
          case "location": {
            const answer = await api.confirmLocation(itemId, input as Inputs["location"], options);
            queryClient.setQueryData(catalogKeys.item(itemId), answer.detail);
            queryClient.setQueryData(
              placeKeys.view(itemId, answer.place.itemVersion),
              answer.place,
            );
            result = answer as Results[K];
            break;
          }
          default:
            throw new Error("unknown place command");
        }
        pendingKey.current = null;
        return { ok: true, result };
      } catch (error) {
        const failure = describePlaceError(error, {
          action: command satisfies PlaceAction,
          lookupAvailable: context.lookupAvailable,
        });
        if (!failure.retryable) pendingKey.current = null;
        const sentVersion = "expectedVersion" in input ? input.expectedVersion : null;
        if (failure.kind !== "conflict" || sentVersion === null) {
          return { ok: false, failure, conflict: null };
        }
        return { ok: false, failure, conflict: await readConflict(sentVersion) };
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [api, itemId, queryClient, readConflict, refreshItem],
  );

  return { run, pending: pendingCount > 0 };
}

/** The sentence under a section after a place command. A conflict says which of the two it was. */
export function describePlaceOutcome(
  outcome: PlaceOutcome<PlaceCommandName>,
  successText: string,
): SaveMessage {
  if (outcome.ok) return { tone: "success", text: successText };
  const { conflict, failure } = outcome;
  if (conflict?.changedElsewhere) {
    return {
      tone: "conflict",
      text: `Changed by someone else. This item moved from version ${conflict.sentVersion} to version ${conflict.latestVersion}. Your entries are still here. Review them and try again.`,
    };
  }
  return { tone: "error", text: failure.message };
}
